import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Moon, Code, ClockClockwise, ArrowsLeftRight } from "@phosphor-icons/react";

function formatMYT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Kuala_Lumpur",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }) + " MYT";
  } catch {
    return String(iso);
  }
}

function formatMYTShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Kuala_Lumpur",
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch {
    return String(iso);
  }
}

function diffCell(a, b) {
  const aNum = typeof a === "number" ? a : Number(a);
  const bNum = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    const delta = bNum - aNum;
    if (delta === 0) return { same: true, delta: 0 };
    return { same: false, delta };
  }
  return { same: JSON.stringify(a) === JSON.stringify(b), delta: null };
}

// Pick the first non-empty value from a list of candidate keys on a row.
function pickCol(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const k of keys) {
    const v = row[k];
    if (v === undefined || v === null || v === "") continue;
    return typeof v === "object" ? JSON.stringify(v) : String(v);
  }
  return "";
}

// Format a date-like string as an MYT date. Leaves plain YYYY-MM-DD as-is.
function formatDateCell(v) {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kuala_Lumpur",
    day: "2-digit", month: "short", year: "numeric",
  });
}

// Fixed summary metrics. Only these 6 numeric pills are shown; any
// non-numeric or extra keys (e.g. by_status, by_group) are ignored so the
// display stays clean and never leaks raw JSON.
const SUMMARY_METRICS = [
  { key: "groups",    label: "Groups",          candidates: ["groups", "group_count", "total_groups"] },
  { key: "total",     label: "Total Shipments", candidates: ["shipments_all_time", "shipments_total", "shipments", "total_shipments"] },
  { key: "today",     label: "Today",           candidates: ["shipments_dated_today", "today", "shipments_today"] },
  { key: "new_today", label: "New Today",       candidates: ["new_shipments_today", "new_today", "new"] },
  { key: "pending",   label: "Pending",         candidates: ["pending_unread", "pending", "unread", "incomplete"] },
  { key: "overdue",   label: "Overdue",         candidates: ["overdue", "overdue_count"] },
];

// Return the first candidate that is numeric-convertible; otherwise null.
function pickNumeric(totals, keys) {
  if (!totals || typeof totals !== "object") return null;
  for (const k of keys) {
    const v = totals[k];
    if (v === undefined || v === null || v === "" || typeof v === "object") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

// Column definitions for the shipments table.
// Primary field (first candidate) matches the external-system spec exactly;
// remaining candidates keep older reports and alternate schemas rendering.
const SHIPMENT_COLS = [
  { key: "date",     label: "Shipment Date",     candidates: ["shipment_date", "date", "doc_date", "flight_date", "created_at", "generated_at"], format: formatDateCell, className: "font-mono text-zinc-800 whitespace-nowrap" },
  { key: "customer", label: "Customer",          candidates: ["customer_name", "customer", "client", "consignee", "consignee_name", "shipper"], className: "text-zinc-900 truncate max-w-[220px]" },
  { key: "awb",      label: "AWB Number",        candidates: ["awb_number", "awb", "awb_no", "hawb", "mawb"], className: "font-mono text-zinc-800 whitespace-nowrap" },
  { key: "from",     label: "From (Origin)",     candidates: ["origin", "from", "from_port", "origin_port", "source", "port_of_origin"], className: "font-mono text-zinc-700" },
  { key: "to",       label: "To (Destination)",  candidates: ["destination", "to", "to_port", "dest", "dest_port", "port_of_destination"], className: "font-mono text-zinc-700" },
  { key: "details",  label: "Record Details",    candidates: ["record_details", "details", "remarks", "notes", "description", "status", "record"], className: "text-zinc-700 truncate max-w-[280px]" },
];

export default function NightlyReport() {
  const [nightly, setNightly] = useState(null); // { exists, report }
  const [nightlyHistory, setNightlyHistory] = useState([]);
  const [showRaw, setShowRaw] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [picked, setPicked] = useState([]); // history_ids, max 2
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, hist] = await Promise.all([
          api.get("/shipments/nightly-report"),
          api.get("/shipments/nightly-report/history").catch(() => ({ data: { reports: [] } })),
        ]);
        setNightly(data);
        setNightlyHistory(hist.data.reports || []);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-refresh nightly report every 15s.
  // We poll unconditionally (even when exists=false) so the FIRST report shows
  // up automatically without the user refreshing the page.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/shipments/nightly-report");
        setNightly((prev) => {
          const prevExists = !!prev?.exists;
          const nextExists = !!data?.exists;
          const prevAt = prev?.report?.received_at;
          const nextAt = data?.report?.received_at;
          const isFirst = !prevExists && nextExists;
          const isNewer = nextExists && nextAt && nextAt !== prevAt;
          if (isFirst || isNewer) {
            toast.success(isFirst ? "First nightly report received" : "New nightly report received", { duration: 4000 });
            api.get("/shipments/nightly-report/history")
              .then((h) => setNightlyHistory(h.data.reports || []))
              .catch(() => {});
          }
          return data;
        });
      } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  // Drop stale picks if history changes
  useEffect(() => {
    if (picked.length === 0) return;
    const stillThere = picked.filter((id) => nightlyHistory.some((r) => r.id === id));
    if (stillThere.length !== picked.length) setPicked(stillThere);
  }, [nightlyHistory]);

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">External Feed</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">
            Nightly Report
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg">
            Latest shipment digest pushed nightly by the external system. Auto-refreshes every 15s.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading report…</div>
      ) : forbidden ? (
        <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500 font-mono" data-testid="nightly-forbidden">
          You do not have permission to view the nightly report feed.
        </div>
      ) : !nightly ? (
        <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500 font-mono">
          Report feed unavailable.
        </div>
      ) : (
        <>
          {/* Latest report */}
          <div className="bg-white border-2 border-indigo-200 mb-6" data-testid="nightly-report-panel">
            <div className="px-5 py-4 border-b border-indigo-100 bg-indigo-50 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Moon size={20} weight="duotone" className="text-indigo-700" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-700 font-mono font-bold">Nightly Report · External Feed</div>
                  <div className="font-display font-black text-lg text-zinc-950 tracking-tight" data-testid="nightly-title">
                    {nightly.exists ? (nightly.report.title || "Nightly Shipment Report") : "No nightly report received yet"}
                  </div>
                </div>
              </div>
              {nightly.exists && (
                <div className="text-xs font-mono text-zinc-600 text-right">
                  <div>
                    Generated: <span className="text-zinc-900" data-testid="nightly-generated-at">
                      {formatMYT(nightly.report.generated_at) || "—"}
                    </span>
                  </div>
                  <div>
                    Received: <span className="text-zinc-900" data-testid="nightly-received-at">
                      {formatMYT(nightly.report.received_at)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {!nightly.exists ? (
              <div className="p-8 text-center text-sm text-zinc-500 font-mono" data-testid="nightly-empty">
                Waiting for the first nightly report from the external system.
              </div>
            ) : (
              <div className="p-5 space-y-4">
                {/* Summary text (must be a string; anything object-y is skipped to
                   avoid leaking raw JSON into the UI). */}
                {typeof nightly.report.summary === "string" && nightly.report.summary && (
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap" data-testid="nightly-summary">
                    {nightly.report.summary}
                  </div>
                )}

                {/* Notes (string only). */}

                {/* Summary metrics — 6 fixed numeric pills. Objects (by_status,
                   by_group, etc) are filtered out so no raw JSON leaks in. */}
                {nightly.report.totals && typeof nightly.report.totals === "object" && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="nightly-totals">
                    {SUMMARY_METRICS.map((m) => {
                      const n = pickNumeric(nightly.report.totals, m.candidates);
                      return (
                        <div key={m.key} className="border border-indigo-100 bg-indigo-50/40 px-3 py-2" data-testid={`nightly-metric-${m.key}`}>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700 font-mono truncate">{m.label}</div>
                          <div className="font-display font-black text-2xl text-zinc-950 tracking-tight">
                            {n === null ? <span className="text-zinc-300">—</span> : n.toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {Array.isArray(nightly.report.items) && nightly.report.items.length > 0 && (
                  <div className="border border-zinc-200 overflow-x-auto" data-testid="nightly-items">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.1em] font-mono text-zinc-500">
                        <tr>
                          {SHIPMENT_COLS.map((c) => (
                            <th key={c.key} className="text-left px-3 py-2" data-testid={`nightly-col-${c.key}`}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {nightly.report.items.slice(0, 100).map((row, i) => (
                          <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`nightly-row-${i}`}>
                            {SHIPMENT_COLS.map((c) => {
                              const raw = pickCol(row, c.candidates);
                              const display = c.format ? c.format(raw) : raw;
                              return (
                                <td key={c.key} className={`px-3 py-2 ${c.className}`} data-testid={`nightly-cell-${i}-${c.key}`} title={display || undefined}>
                                  {display || <span className="text-zinc-300">—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {nightly.report.items.length > 100 && (
                      <div className="px-3 py-2 text-xs font-mono text-zinc-500 border-t border-zinc-200">
                        Showing 100 of {nightly.report.items.length} shipments — see raw JSON for full data.
                      </div>
                    )}
                  </div>
                )}

                {typeof nightly.report.notes === "string" && nightly.report.notes && (
                  <div className="text-xs text-zinc-700 bg-amber-50 border border-amber-200 px-3 py-2 whitespace-pre-wrap" data-testid="nightly-notes">
                    {nightly.report.notes}
                  </div>
                )}

                <div>
                  <button
                    onClick={() => setShowRaw((v) => !v)}
                    data-testid="nightly-toggle-raw"
                    className="text-xs text-[#002FA7] hover:underline flex items-center gap-1 font-mono"
                  >
                    <Code size={12} /> {showRaw ? "Hide raw JSON" : "View raw JSON"}
                  </button>
                  {showRaw && (
                    <pre
                      className="mt-2 text-[11px] font-mono bg-zinc-950 text-zinc-100 p-3 overflow-x-auto max-h-80"
                      data-testid="nightly-raw"
                    >
                      {JSON.stringify(nightly.report.raw ?? nightly.report, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* History browser + diff */}
          {nightly?.exists && nightlyHistory.length > 0 && (
            <div className="bg-white border border-zinc-200 mb-6" data-testid="nightly-history-panel">
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-zinc-50 transition-colors duration-150"
                data-testid="nightly-history-toggle"
              >
                <div className="flex items-center gap-2">
                  <ClockClockwise size={18} weight="duotone" className="text-indigo-700" />
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Report History</div>
                    <div className="font-display font-black text-sm text-zinc-950">
                      Last {nightlyHistory.length} nightly reports · pick 2 to diff
                    </div>
                  </div>
                </div>
                <div className="text-xs text-[#002FA7] font-mono">
                  {showHistory ? "Hide" : "Browse"}
                </div>
              </button>

              {showHistory && (
                <div className="border-t border-zinc-200 p-5 space-y-4">
                  <div className="text-[11px] text-zinc-500 font-mono">
                    Tick up to 2 rows to compare them side-by-side. Uncheck to reset.
                  </div>
                  <div className="border border-zinc-200 max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
                        <tr>
                          <th className="w-10 px-2 py-2"></th>
                          <th className="text-left px-3 py-2">Received (MYT)</th>
                          <th className="text-left px-3 py-2">Generated</th>
                          <th className="text-left px-3 py-2">Title</th>
                          <th className="text-right px-3 py-2">Items</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nightlyHistory.map((r) => {
                          const checked = picked.includes(r.id);
                          const disabled = !checked && picked.length >= 2;
                          const items = Array.isArray(r.items) ? r.items.length : 0;
                          return (
                            <tr
                              key={r.id}
                              className={`border-b border-zinc-100 ${checked ? "bg-indigo-50/60" : "hover:bg-zinc-50"}`}
                              data-testid={`nightly-history-row-${r.id}`}
                            >
                              <td className="px-2 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={(e) => {
                                    setPicked((prev) =>
                                      e.target.checked ? [...prev, r.id].slice(-2) : prev.filter((x) => x !== r.id)
                                    );
                                  }}
                                  data-testid={`nightly-history-pick-${r.id}`}
                                  className="accent-indigo-600"
                                />
                              </td>
                              <td className="px-3 py-2 font-mono text-zinc-800">{formatMYTShort(r.received_at)}</td>
                              <td className="px-3 py-2 font-mono text-zinc-600">{formatMYTShort(r.generated_at)}</td>
                              <td className="px-3 py-2 text-zinc-700 truncate max-w-[280px]">{r.title || "—"}</td>
                              <td className="px-3 py-2 font-mono text-zinc-700 text-right">{items}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {picked.length === 2 && (() => {
                    const [leftId, rightId] = picked;
                    const a = nightlyHistory.find((r) => r.id === leftId);
                    const b = nightlyHistory.find((r) => r.id === rightId);
                    if (!a || !b) return null;
                    const [L, R] = new Date(a.received_at) <= new Date(b.received_at) ? [a, b] : [b, a];
                    const totalsKeys = Array.from(
                      new Set([
                        ...Object.keys((L.totals && typeof L.totals === "object") ? L.totals : {}),
                        ...Object.keys((R.totals && typeof R.totals === "object") ? R.totals : {}),
                      ])
                    );
                    return (
                      <div className="border-t border-zinc-200 pt-4" data-testid="nightly-diff">
                        <div className="flex items-center gap-2 mb-3">
                          <ArrowsLeftRight size={16} weight="duotone" className="text-indigo-700" />
                          <div className="font-display font-black text-sm text-zinc-950">Side-by-side comparison</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4 text-xs font-mono">
                          <div className="border border-zinc-200 p-3">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Older</div>
                            <div className="text-zinc-900 mt-1" data-testid="nightly-diff-left-received">{formatMYT(L.received_at)}</div>
                            <div className="text-zinc-600 truncate">{L.title || "—"}</div>
                          </div>
                          <div className="border border-indigo-200 p-3 bg-indigo-50/40">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700">Newer</div>
                            <div className="text-zinc-900 mt-1" data-testid="nightly-diff-right-received">{formatMYT(R.received_at)}</div>
                            <div className="text-zinc-600 truncate">{R.title || "—"}</div>
                          </div>
                        </div>

                        {totalsKeys.length > 0 && (
                          <div className="border border-zinc-200 mb-4" data-testid="nightly-diff-totals">
                            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">
                              Totals · delta
                            </div>
                            <table className="w-full text-sm">
                              <thead className="border-b border-zinc-100 text-[10px] uppercase font-mono text-zinc-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Metric</th>
                                  <th className="text-right px-3 py-2">Older</th>
                                  <th className="text-right px-3 py-2">Newer</th>
                                  <th className="text-right px-3 py-2">Δ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {totalsKeys.map((k) => {
                                  const lv = (L.totals || {})[k];
                                  const rv = (R.totals || {})[k];
                                  const d = diffCell(lv, rv);
                                  const showL = typeof lv === "object" ? JSON.stringify(lv) : String(lv ?? "—");
                                  const showR = typeof rv === "object" ? JSON.stringify(rv) : String(rv ?? "—");
                                  const cls = d.same
                                    ? "text-zinc-500"
                                    : d.delta === null
                                      ? "text-amber-700"
                                      : d.delta > 0
                                        ? "text-emerald-700"
                                        : "text-red-700";
                                  const deltaText = d.same
                                    ? "="
                                    : d.delta === null
                                      ? "changed"
                                      : (d.delta > 0 ? "+" : "") + String(d.delta);
                                  return (
                                    <tr key={k} className="border-b border-zinc-100" data-testid={`nightly-diff-row-${k}`}>
                                      <td className="px-3 py-2 font-mono text-zinc-800">{k}</td>
                                      <td className="px-3 py-2 font-mono text-zinc-700 text-right">{showL}</td>
                                      <td className="px-3 py-2 font-mono text-zinc-700 text-right">{showR}</td>
                                      <td className={`px-3 py-2 font-mono text-right ${cls}`}>{deltaText}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="border border-zinc-200 p-3">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-1">Items count</div>
                            <div className="font-display font-black text-2xl">{Array.isArray(L.items) ? L.items.length : 0}</div>
                            {L.notes && (
                              <div className="mt-2 text-[11px] text-zinc-700 whitespace-pre-wrap">
                                {typeof L.notes === "string" ? L.notes : JSON.stringify(L.notes)}
                              </div>
                            )}
                          </div>
                          <div className="border border-indigo-200 p-3 bg-indigo-50/30">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700 font-mono mb-1">Items count</div>
                            <div className="font-display font-black text-2xl">{Array.isArray(R.items) ? R.items.length : 0}</div>
                            {R.notes && (
                              <div className="mt-2 text-[11px] text-zinc-700 whitespace-pre-wrap">
                                {typeof R.notes === "string" ? R.notes : JSON.stringify(R.notes)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {picked.length === 1 && (
                    <div className="text-[11px] text-zinc-500 font-mono">
                      Pick one more report to see the diff.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
